// Public capability transport only. Do not route through the staff-auth client.
export const PUBLIC_RECEIPT_TIMEOUT_MS = 30000;
const unavailable = () => new Error('Receipt not available.');
const record = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = value => value == null || typeof value === 'string' || (typeof value === 'number' && Number.isFinite(value));

export function validatePublicReceipt(value, orderId) {
  if (!record(value) || value.order_id !== orderId || !record(value.confirmation)) throw unavailable();
  const fields = ['internal_order_number', 'recipient_name', 'recipient_street_address',
    'recipient_street_address_2', 'recipient_city', 'recipient_state', 'recipient_zip_code',
    'delivery_date', 'resolved_source', 'driver_name'];
  const confirmationFields = ['received_by', 'received_by_relationship', 'time_delivered',
    'confirmed_at', 'time_confirmed', 'confirmed_by_user_name', 'notes'];
  if (fields.some(key => !text(value[key])) || confirmationFields.some(key => !text(value.confirmation[key]))) throw unavailable();
  const c = value.confirmation;
  const files = c.media_files ?? [];
  const history = c.edit_history ?? [];
  if (!Array.isArray(files) || files.some(file => !record(file)
    || (file.file_type != null && typeof file.file_type !== 'string')
    || (file.content_type != null && typeof file.content_type !== 'string'))
    || !Array.isArray(history) || history.some(entry => !record(entry)
      || ['field', 'user', 'timestamp'].some(key => !text(entry[key])))
    || (value.has_arrangement_photo != null && typeof value.has_arrangement_photo !== 'boolean')) throw unavailable();
  if (c.gps_location != null && (!record(c.gps_location)
    || ['latitude', 'longitude', 'accuracy'].some(key => !text(c.gps_location[key])))) throw unavailable();
  // Select presentation fields; never carry storage identities or extra response facts.
  return {
    order_id: orderId,
    ...Object.fromEntries(fields.map(key => [key, value[key]])),
    has_arrangement_photo: value.has_arrangement_photo === true,
    confirmation: {
      ...Object.fromEntries(confirmationFields.map(key => [key, c[key]])),
      gps_location: c.gps_location == null ? null : Object.fromEntries(
        ['latitude', 'longitude', 'accuracy'].map(key => [key, c.gps_location[key]])),
      media_files: files.map(file => ({ file_type: file.file_type, content_type: file.content_type })),
      edit_history: history.map(entry => Object.fromEntries(['field', 'user', 'timestamp'].map(key => [key, entry[key]]))),
    },
  };
}

// Deadline covers fetch AND body consumption; ignored aborts cannot settle twice.
// Object URLs are deliberately created only by the still-current UI owner.
export function readPublicReceiptResource(url, token, { signal, media = false } = {}) {
  return new Promise((resolve, reject) => {
    const controller = new AbortController();
    let settled = false;
    let timer;
    const finish = (value, error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener('abort', cancel);
      controller.abort();
      if (error) reject(error); else resolve(value);
    };
    const cancel = () => finish(null, new DOMException('Receipt request cancelled', 'AbortError'));
    if (signal?.aborted) { cancel(); return; }
    if (!token) { finish(null, unavailable()); return; }
    signal?.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(() => finish(null, new Error('Loading timed out. Please try again.')), PUBLIC_RECEIPT_TIMEOUT_MS);
    Promise.resolve().then(async () => {
      if (settled) return null;
      const response = await fetch(url, {
        headers: { 'X-POD-Token': token }, signal: controller.signal,
        credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer',
      });
      if (settled) return null;
      if (!response.ok) throw unavailable();
      const value = await (media ? response.blob() : response.json());
      if (media && (!(value instanceof Blob) || value.size === 0)) throw unavailable();
      return value;
    }).then(value => finish(value), () => finish(null, unavailable()));
  });
}
